import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { submitBackCheckin } from "@/api/back";
import { BackCheckInForm } from "@/components/back/BackCheckInForm";
import { Card, SectionLabel } from "@/components/ui/Card";
import type { BackCheckinResponse } from "@/types";

export default function BackCheckIn() {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BackCheckinResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();

  async function handleSubmit(payload: any) {
    setSubmitting(true);
    setError(null);
    try {
      const r = await submitBackCheckin(payload);
      setResult(r);
      if (!r.warning && r.red_flags.length === 0) {
        setTimeout(() => nav("/", { replace: true }), 1200);
      }
    } catch {
      setError(t("back_checkin.submit_failed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>{t("back_checkin.section_label")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium">{t("back_checkin.h1")}</h1>
        <p className="mt-1 text-[13px] text-taupe">
          {t("back_checkin.subtitle")}
        </p>
      </div>

      {result ? (
        <ResultCard result={result} />
      ) : null}

      {error ? (
        <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">{error}</div>
      ) : null}

      <BackCheckInForm onSubmit={handleSubmit} submitting={submitting} />
    </div>
  );
}

function ResultCard({ result }: { result: BackCheckinResponse }) {
  const emergency = result.red_flags.some((f) => f.startsWith("EMERGENCY"));
  if (emergency) {
    return (
      <Card className="border-coral bg-coral/10">
        <SectionLabel className="text-coral">🛑 Срочно</SectionLabel>
        <p className="mt-2 text-[14px] text-coral">{result.warning}</p>
      </Card>
    );
  }
  if (result.warning) {
    return (
      <Card className="border-amber/50 bg-amber/10">
        <SectionLabel className="text-amber-dark">Внимание</SectionLabel>
        <p className="mt-2 text-[14px] text-navy-deep">{result.warning}</p>
      </Card>
    );
  }
  return (
    <Card>
      <SectionLabel>Сохранено</SectionLabel>
      <p className="mt-2 text-[14px] text-taupe">Чек-ин записан. Возвращаемся на главную…</p>
    </Card>
  );
}
